<template>
    <analytics-sidebar-box :canReset="startDate !== undefined || endDate !== undefined" @reset="reset">
        <template v-slot:title>Modified Date</template>

        <div class="relative flex flex-col gap-3">
            <div
                v-for="option in rangeOptions"
                :key="option.value"
                class="flex gap-2 items-center text-xs cursor-pointer"
                :data-test="`modified-date-${option.value}`"
                tabindex="0"
                @click.prevent="handlePresetClick(option.value)"
                @keydown.space.prevent="handlePresetClick(option.value)"
                @keydown.enter.prevent="handlePresetClick(option.value)"
            >
                <vx-radio :modelValue="selectedPreset?.value === option.value"></vx-radio>
                {{ option.label }}
            </div>
        </div>

        <transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="opacity-0 -translate-y-2"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition duration-150 ease-in"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 -translate-y-2"
        >
            <div v-if="isDatePickerOpened" class="absolute z-20 w-full" @click.stop>
                <vx-date-picker
                    mode="date"
                    :modelValue="datePickerRange"
                    @update:modelValue="handleDatePickerRange"
                    is-range
                    v-on-click-outside="() => closeDatePickerRange()"
                    :timezone="getClientTz()"
                    :maxDate="new Date()"
                />
            </div>
        </transition>
    </analytics-sidebar-box>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { VxRadio, VxDatePicker } from '@voxie/frontend-components';
import { vOnClickOutside } from '@vueuse/components';
import AnalyticsSidebarBox from '../../partial/sidebar/AnalyticsSidebarBox.vue';
import dayjs from '~/utils/dayjs';
import { formatDateForRange } from '~/utils/analytics';
import { getClientTz, shortDate } from '~/utils/date';

type PresetValue = 'today' | 'last_2_days' | 'last_3_days' | 'current_week' | 'custom' | 'selected_custom';

const startDate = defineModel<string | undefined>('startDate', { required: true });
const endDate = defineModel<string | undefined>('endDate', { required: true });

const defaultPresetOptions: { value: PresetValue; label: string }[] = [
    { value: 'today', label: 'Today' },
    { value: 'last_2_days', label: 'Last 2 Days' },
    { value: 'last_3_days', label: 'Last 3 Days' },
    { value: 'current_week', label: 'Current Week' },
];

const isDatePickerOpened = ref(false);

const computePresetRange = (preset: PresetValue): [string, string] | undefined => {
    const now = dayjs().tz(getClientTz());

    switch (preset) {
        case 'today':
            return [formatDateForRange(now.startOf('day')), formatDateForRange(now.add(1, 'day').startOf('day'))];
        case 'last_2_days':
            return [
                formatDateForRange(now.subtract(1, 'day').startOf('day')),
                formatDateForRange(now.add(1, 'day').startOf('day')),
            ];
        case 'last_3_days':
            return [
                formatDateForRange(now.subtract(2, 'days').startOf('day')),
                formatDateForRange(now.add(1, 'day').startOf('day')),
            ];
        case 'current_week':
            return [formatDateForRange(now.startOf('isoWeek')), formatDateForRange(now.add(1, 'day').startOf('day'))];
        default:
            return undefined;
    }
};

const selectedPreset = computed<{ value: PresetValue; label: string } | null>(() => {
    if (!startDate.value || !endDate.value) {
        return null;
    }

    const start = dayjs.utc(startDate.value).tz(getClientTz());
    const end = dayjs.utc(endDate.value).tz(getClientTz());

    const matchedPreset = defaultPresetOptions.find((option) => {
        const range = computePresetRange(option.value);
        if (!range) {
            return false;
        }
        const presetStart = dayjs.utc(range[0]).tz(getClientTz());
        const presetEnd = dayjs.utc(range[1]).tz(getClientTz());
        return start.isSame(presetStart, 'day') && end.isSame(presetEnd, 'day');
    });

    if (matchedPreset) {
        return matchedPreset;
    }

    return {
        value: 'selected_custom',
        label: `${shortDate(start)} - ${shortDate(end.subtract(1, 'day'))}`,
    };
});

const customRangeOption = computed<{ value: PresetValue; label: string }>(() => {
    if (selectedPreset.value?.value === 'selected_custom') {
        return selectedPreset.value;
    }
    return { value: 'custom', label: 'Custom' };
});

const rangeOptions = computed(() => {
    return [...defaultPresetOptions, { ...customRangeOption.value }];
});

const datePickerRange = computed(() => {
    if (!startDate.value || !endDate.value) {
        return undefined;
    }

    return {
        start: dayjs.utc(startDate.value).toDate(),
        // end has +1 day added for exclusive backend query, subtract to show correct date in picker
        end: dayjs.utc(endDate.value).subtract(1, 'day').toDate(),
    };
});

const setRange = (range: [string, string] | undefined) => {
    if (range) {
        startDate.value = range[0];
        endDate.value = range[1];
    } else {
        startDate.value = undefined;
        endDate.value = undefined;
    }
};

const handlePresetClick = (preset: PresetValue) => {
    if (preset === 'custom' || preset === 'selected_custom') {
        isDatePickerOpened.value = true;
        return;
    }

    setRange(computePresetRange(preset));
};

const handleDatePickerRange = ({ start, end }: { start: Date; end: Date }) => {
    const startDayjs = dayjs(start).tz(getClientTz()).startOf('day');
    const endDayjs = dayjs(end).tz(getClientTz()).add(1, 'day').startOf('day');

    setRange([formatDateForRange(startDayjs), formatDateForRange(endDayjs)]);
    isDatePickerOpened.value = false;
};

const closeDatePickerRange = () => {
    isDatePickerOpened.value = false;
};

const reset = () => {
    setRange(undefined);
    isDatePickerOpened.value = false;
};
</script>
